import { z } from "zod"
import { optionalEmailSchema } from "../common/email-validator"
import { phoneNumberSchema } from "../common/phone-number-validator"
import { TranslationKey } from "../i18n/translation-key"

export interface CompanyContactPerson {
  id: string
  firstName: string
  lastName: string | null
  jobTitle: string | null
  phoneNumber: string | null
  email: string | null
  departmentId: string | null
}

/**
 * An "Ansvarig" of a company or avdelning. Every job message to a contact
 * person in that exact unit is also sent to each of the unit's Ansvariga.
 */
export interface CompanyResponsible {
  id: string
  name: string | null
  phoneNumber: string | null
  email: string | null
}

export type ConfirmationMessageSource =
  | { kind: "store" }
  | { kind: "company"; name: string }
  | { kind: "department"; name: string }

/**
 * The booking confirmation a company or avdelning uses while its own is
 * switched off: the nearest parent's, then the company's, then the store's.
 */
export interface InheritedConfirmationMessage {
  message: string
  source: ConfirmationMessageSource
}

interface CustomConfirmationMessage {
  customConfirmationMessageEnabled: boolean
  // Kept while switched off, so switching back on restores it.
  customConfirmationMessage: string | null
  inheritedConfirmationMessage: InheritedConfirmationMessage
}

export interface Department extends CustomConfirmationMessage {
  id: string
  name: string
  email: string | null
  responsibles: CompanyResponsible[]
  parentId: string | null
  children: Department[]
  contactPersons: CompanyContactPerson[]
}

export interface Company extends CustomConfirmationMessage {
  id: string
  name: string
  email: string | null
  organizationNumber: string | null
  specterCustomerId: string | null
  responsibles: CompanyResponsible[]
  storeId: string
  priceListId: string | null
  priceListName: string | null
  levelNames: string[]
  departments: Department[]
  contactPersons: CompanyContactPerson[]
}

// Copies can only be sent over a phone number or an email — a name alone
// would look configured while nothing ever reaches the Ansvarig.
export const CompanyResponsibleInputSchema = z
  .object({
    name: z.string().max(255).nullish(),
    phoneNumber: phoneNumberSchema()
      .and(z.string().max(20))
      .or(z.literal(""))
      .nullish(),
    email: optionalEmailSchema().nullish(),
  })
  .refine((value) => Boolean(value.phoneNumber) || Boolean(value.email), {
    path: ["phoneNumber"],
    message:
      "common.validation.contactForCopiesRequired" satisfies TranslationKey,
  })
export type CompanyResponsibleInput = z.infer<
  typeof CompanyResponsibleInputSchema
>

// Left out, the unit's Ansvariga stay as they are; sent, the list replaces them.
const responsibleFields = {
  responsibles: z.array(CompanyResponsibleInputSchema).optional(),
}

const customConfirmationMessageFields = {
  customConfirmationMessageEnabled: z.boolean().optional(),
  customConfirmationMessage: z.string().max(5000).nullish(),
}

interface CustomConfirmationMessageInput {
  customConfirmationMessageEnabled?: boolean
  customConfirmationMessage?: string | null
}

// Switched on with no text, the customer would get a confirmation holding
// nothing but the rebooking footer.
const hasMessageWhenEnabled = (value: CustomConfirmationMessageInput) =>
  !value.customConfirmationMessageEnabled ||
  Boolean(value.customConfirmationMessage?.trim())

const missingCustomConfirmationMessage = {
  path: ["customConfirmationMessage"],
  message:
    "common.validation.customConfirmationMessageRequired" satisfies TranslationKey,
}

const CompanyBaseSchema = z.object({
  name: z.string().min(1).max(255),
  email: optionalEmailSchema().nullish(),
  organizationNumber: z.string().max(50).nullish(),
  specterCustomerId: z
    .string()
    .trim()
    .regex(
      /^\d*$/,
      "common.validation.specterIdDigits" satisfies TranslationKey,
    )
    .max(50)
    .nullish(),
  priceListId: z.string().uuid().nullish(),
  levelNames: z.array(z.string().min(1).max(255)).min(1).default(["Avdelning"]),
  ...responsibleFields,
})

export const CreateCompanySchema = CompanyBaseSchema
export type CreateCompany = z.infer<typeof CreateCompanySchema>

export const UpdateCompanySchema = CompanyBaseSchema.partial()
  .extend(customConfirmationMessageFields)
  .refine(hasMessageWhenEnabled, missingCustomConfirmationMessage)
export type UpdateCompany = z.infer<typeof UpdateCompanySchema>

export const CreateDepartmentSchema = z.object({
  name: z.string().min(1).max(255),
  email: optionalEmailSchema().nullish(),
  parentId: z.string().uuid().nullish(),
  ...responsibleFields,
})
export type CreateDepartment = z.infer<typeof CreateDepartmentSchema>

export const UpdateDepartmentSchema = z
  .object({
    name: z.string().min(1).max(255).optional(),
    email: optionalEmailSchema().nullish(),
    ...responsibleFields,
    ...customConfirmationMessageFields,
  })
  .refine(hasMessageWhenEnabled, missingCustomConfirmationMessage)
export type UpdateDepartment = z.infer<typeof UpdateDepartmentSchema>

export const MoveDepartmentSchema = z.object({
  parentId: z.string().uuid().nullable(),
})
export type MoveDepartment = z.infer<typeof MoveDepartmentSchema>

export const CreateCompanyContactPersonSchema = z.object({
  firstName: z.string().min(1).max(255),
  lastName: z.string().max(255).nullish(),
  jobTitle: z.string().max(255).nullish(),
  phoneNumber: phoneNumberSchema().and(z.string().max(20)),
  email: optionalEmailSchema().nullish(),
  departmentId: z.string().uuid().nullish(),
})
export type CreateCompanyContactPerson = z.infer<
  typeof CreateCompanyContactPersonSchema
>

export const UpdateCompanyContactPersonSchema =
  CreateCompanyContactPersonSchema.partial()
export type UpdateCompanyContactPerson = z.infer<
  typeof UpdateCompanyContactPersonSchema
>

export const BulkMarkInvoicedSchema = z.object({
  jobIds: z.array(z.string().uuid()).min(1),
})
export type BulkMarkInvoiced = z.infer<typeof BulkMarkInvoicedSchema>

export const UpdateReferenceSchema = z.object({
  reference: z.string().max(500).nullable(),
  // Only accepted on company jobs in Specter stores; omit to leave unchanged.
  eServiceNumber: z.string().trim().max(100).nullable().optional(),
})
export type UpdateReference = z.infer<typeof UpdateReferenceSchema>

export const UpdateJobCompanyContactPersonSchema = z.object({
  companyContactPersonId: z.string().uuid().nullable(),
})
export type UpdateJobCompanyContactPerson = z.infer<
  typeof UpdateJobCompanyContactPersonSchema
>
